/**
 * Document Service
 * Handles document CRUD operations with encryption
 */

import { supabase } from '@xanectech/supabase-client';
import { createLogger } from '@xanectech/lib';
import type { Json } from '@xanectech/supabase-client';
import { encryptDataWithMasterKey } from '@xanectech/encryption';
import type {
  UserDocument,
  DocumentFolder,
  StorageUsage,
  DocumentTag,
  DocumentType,
} from '@xanectech/types';

const logger = createLogger('Data - DocumentService');

// Helper to transform DB response to our types
function transformFolder(data: Record<string, unknown>): DocumentFolder {
  return {
    id: data.id as string,
    user_id: data.user_id as string,
    parent_folder_id: data.parent_folder_id as string | null,
    name: data.name as string,
    description: data.description as string | null,
    color_theme: data.color_theme as string | null,
    icon: data.icon as string | null,
    is_system_folder: (data.is_system_folder as boolean) ?? false,
    display_order: (data.display_order as number) ?? 0,
    created_at: data.created_at as string,
    updated_at: data.updated_at as string,
  };
}

function transformDocument(data: Record<string, unknown>): UserDocument {
  return {
    id: data.id as string,
    user_id: data.user_id as string,
    folder_id: data.folder_id as string | null,
    file_name: data.file_name as string,
    file_extension: data.file_extension as string | null,
    mime_type: data.mime_type as string | null,
    file_size_bytes: data.file_size_bytes as number,
    storage_path: data.storage_path as string,
    storage_bucket: (data.storage_bucket as string) ?? 'user-documents',
    is_encrypted: (data.is_encrypted as boolean) ?? true,
    encryption_algorithm: data.encryption_algorithm as string | null,
    encrypted_key: data.encrypted_key as string | null,
    iv: data.iv as string | null,
    document_type: data.document_type as string | null,
    title: data.title as string | null,
    description: data.description as string | null,
    tags: data.tags as string[] | null,
    version: (data.version as number) ?? 1,
    parent_version_id: data.parent_version_id as string | null,
    is_latest_version: (data.is_latest_version as boolean) ?? true,
    status: (data.status as string) ?? 'active',
    archived_at: data.archived_at as string | null,
    deleted_at: data.deleted_at as string | null,
    created_at: data.created_at as string,
    updated_at: data.updated_at as string,
    last_accessed_at: data.last_accessed_at as string | null,
  };
}

function transformStorageUsage(
  data: Record<string, unknown>,
  userId: string,
): StorageUsage {
  const usageByType = data.usage_by_type as Json;
  return {
    id: (data.id as string) ?? '',
    user_id: userId,
    total_bytes_used: (data.total_bytes_used as number) ?? 0,
    document_count: (data.document_count as number) ?? 0,
    storage_limit_bytes: (data.storage_limit_bytes as number) ?? 104857600,
    usage_by_type:
      typeof usageByType === 'object' &&
      usageByType !== null &&
      !Array.isArray(usageByType)
        ? (usageByType as Record<string, number>)
        : {},
    last_calculated_at:
      (data.last_calculated_at as string) ?? new Date().toISOString(),
    updated_at: (data.updated_at as string) ?? new Date().toISOString(),
  };
}

function transformTag(data: Record<string, unknown>): DocumentTag {
  return {
    id: data.id as string,
    user_id: data.user_id as string,
    name: data.name as string,
    color: data.color as string | null,
    usage_count: (data.usage_count as number) ?? 0,
    created_at: data.created_at as string,
  };
}

// ========== Folder Operations ==========

export async function getFolders(): Promise<DocumentFolder[]> {
  const { data, error } = await supabase
    .from('user_document_folders')
    .select('*')
    .order('display_order', { ascending: true });

  if (error) {
    logger.error('Failed to fetch folders', error);
    throw error;
  }

  return (data || []).map((d) =>
    transformFolder(d as unknown as Record<string, unknown>),
  );
}

export async function createFolder(folder: {
  name: string;
  parent_folder_id?: string | null;
  description?: string;
  color_theme?: string;
  icon?: string;
}): Promise<DocumentFolder> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('user_document_folders')
    .insert({
      user_id: user.user.id,
      name: folder.name,
      parent_folder_id: folder.parent_folder_id || null,
      description: folder.description || null,
      color_theme: folder.color_theme || null,
      icon: folder.icon || null,
    })
    .select()
    .single();

  if (error) {
    logger.error('Failed to create folder', error);
    throw error;
  }

  return transformFolder(data as unknown as Record<string, unknown>);
}

export async function updateFolder(
  folderId: string,
  updates: Partial<
    Pick<
      DocumentFolder,
      'name' | 'description' | 'color_theme' | 'icon' | 'parent_folder_id'
    >
  >,
): Promise<DocumentFolder> {
  const { data, error } = await supabase
    .from('user_document_folders')
    .update(updates)
    .eq('id', folderId)
    .select()
    .single();

  if (error) {
    logger.error('Failed to update folder', error);
    throw error;
  }

  return transformFolder(data as unknown as Record<string, unknown>);
}

export async function deleteFolder(folderId: string): Promise<void> {
  const { error } = await supabase
    .from('user_document_folders')
    .delete()
    .eq('id', folderId);

  if (error) {
    logger.error('Failed to delete folder', error);
    throw error;
  }
}

// ========== Document Operations ==========

export async function getDocuments(options?: {
  folderId?: string | null;
  status?: string;
  documentType?: DocumentType;
  tags?: string[];
  search?: string;
}): Promise<UserDocument[]> {
  let query = supabase
    .from('user_documents')
    .select('*')
    .eq('is_latest_version', true)
    .order('created_at', { ascending: false });

  if (options?.folderId !== undefined) {
    if (options.folderId === null) {
      query = query.is('folder_id', null);
    } else {
      query = query.eq('folder_id', options.folderId);
    }
  }

  if (options?.status) {
    query = query.eq('status', options.status);
  } else {
    query = query.eq('status', 'active');
  }

  if (options?.documentType) {
    query = query.eq('document_type', options.documentType);
  }

  if (options?.tags && options.tags.length > 0) {
    query = query.overlaps('tags', options.tags);
  }

  if (options?.search) {
    query = query.or(
      `title.ilike.%${options.search}%,file_name.ilike.%${options.search}%`,
    );
  }

  const { data, error } = await query;

  if (error) {
    logger.error('Failed to fetch documents', error);
    throw error;
  }

  return (data || []).map((d) =>
    transformDocument(d as unknown as Record<string, unknown>),
  );
}

export async function getDocument(
  documentId: string,
): Promise<UserDocument | null> {
  const { data, error } = await supabase
    .from('user_documents')
    .select('*')
    .eq('id', documentId)
    .maybeSingle();

  if (error) {
    logger.error('Failed to fetch document', error);
    throw error;
  }

  return data
    ? transformDocument(data as unknown as Record<string, unknown>)
    : null;
}

export async function updateDocument(
  documentId: string,
  updates: Partial<
    Pick<
      UserDocument,
      'title' | 'description' | 'document_type' | 'tags' | 'folder_id'
    >
  >,
): Promise<UserDocument> {
  const { data, error } = await supabase
    .from('user_documents')
    .update(updates)
    .eq('id', documentId)
    .select()
    .single();

  if (error) {
    logger.error('Failed to update document', error);
    throw error;
  }

  return transformDocument(data as unknown as Record<string, unknown>);
}

export async function archiveDocument(documentId: string): Promise<void> {
  const { error } = await supabase
    .from('user_documents')
    .update({
      status: 'archived',
      archived_at: new Date().toISOString(),
    })
    .eq('id', documentId);

  if (error) {
    logger.error('Failed to archive document', error);
    throw error;
  }
}

export async function restoreDocument(documentId: string): Promise<void> {
  const { error } = await supabase
    .from('user_documents')
    .update({
      status: 'active',
      archived_at: null,
    })
    .eq('id', documentId);

  if (error) {
    logger.error('Failed to restore document', error);
    throw error;
  }
}

export async function deleteDocument(documentId: string): Promise<void> {
  // Get document to find storage path
  const document = await getDocument(documentId);
  if (!document) throw new Error('Document not found');

  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from(document.storage_bucket)
    .remove([document.storage_path]);

  if (storageError) {
    logger.error('Failed to delete document from storage', storageError);
  }

  // Delete from database
  const { error } = await supabase
    .from('user_documents')
    .delete()
    .eq('id', documentId);

  if (error) {
    logger.error('Failed to delete document', error);
    throw error;
  }
}

export async function moveDocuments(
  documentIds: string[],
  folderId: string | null,
): Promise<void> {
  if (documentIds.length === 0) return;

  const { error } = await supabase
    .from('user_documents')
    .update({ folder_id: folderId })
    .in('id', documentIds);

  if (error) {
    logger.error('Failed to move documents', error);
    throw error;
  }
}

// ========== Storage Usage ==========

export async function getStorageUsage(): Promise<StorageUsage | null> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('user_storage_usage')
    .select('*')
    .eq('user_id', user.user.id)
    .maybeSingle();

  if (error) {
    logger.error('Failed to fetch storage usage', error);
    throw error;
  }

  if (!data) {
    return {
      id: '',
      user_id: user.user.id,
      total_bytes_used: 0,
      document_count: 0,
      storage_limit_bytes: 104857600,
      usage_by_type: {},
      last_calculated_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  }

  return transformStorageUsage(
    data as unknown as Record<string, unknown>,
    user.user.id as string,
  );
}

export async function getStorageLimit(): Promise<number> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error('Not authenticated');

  const { data, error } = await supabase.rpc('get_user_storage_limit', {
    p_user_id: user.user.id,
  });

  if (error) {
    logger.error('Failed to fetch storage limit', error);
    return 104857600;
  }

  return data || 104857600;
}

// ========== Tags ==========

export async function getTags(): Promise<DocumentTag[]> {
  const { data, error } = await supabase
    .from('document_tags')
    .select('*')
    .order('usage_count', { ascending: false });

  if (error) {
    logger.error('Failed to fetch tags', error);
    throw error;
  }

  return (data || []).map((d) =>
    transformTag(d as unknown as Record<string, unknown>),
  );
}

export async function createTag(
  name: string,
  color?: string,
): Promise<DocumentTag> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('document_tags')
    .insert({
      user_id: user.user.id,
      name,
      color: color || null,
    })
    .select()
    .single();

  if (error) {
    logger.error('Failed to create tag', error);
    throw error;
  }

  return transformTag(data as unknown as Record<string, unknown>);
}

// ========== Utility Functions ==========

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

export function getFileExtension(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  return ext;
}

export function getMimeType(filename: string): string {
  const ext = getFileExtension(filename);
  const mimeTypes: Record<string, string> = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    txt: 'text/plain',
    csv: 'text/csv',
    json: 'application/json',
    xml: 'application/xml',
    zip: 'application/zip',
    rar: 'application/x-rar-compressed',
  };

  return mimeTypes[ext] || 'application/octet-stream';
}

export function getDocumentTypeIcon(
  type: DocumentType | string | null,
): string {
  const icons: Record<string, string> = {
    will: '📜',
    medical: '🏥',
    financial: '💰',
    legal: '⚖️',
    personal: '👤',
    other: '📄',
  };

  return icons[type || 'other'] || '📄';
}

export function getFileTypeIcon(
  mimeType: string | null,
  extension: string | null,
): string {
  if (!mimeType && !extension) return '📄';

  const type = mimeType?.split('/')[0];
  const ext = extension?.toLowerCase();

  if (type === 'image') return '🖼️';
  if (mimeType?.includes('pdf')) return '📕';
  if (mimeType?.includes('word') || ext === 'doc' || ext === 'docx')
    return '📘';
  if (
    mimeType?.includes('excel') ||
    mimeType?.includes('spreadsheet') ||
    ext === 'xls' ||
    ext === 'xlsx'
  )
    return '📗';
  if (
    mimeType?.includes('powerpoint') ||
    mimeType?.includes('presentation') ||
    ext === 'ppt' ||
    ext === 'pptx'
  )
    return '📙';
  if (type === 'text' || ext === 'txt' || ext === 'csv') return '📝';
  if (mimeType?.includes('zip') || mimeType?.includes('rar')) return '📦';

  return '📄';
}
